/** Reklamation modal — pixel-perfect 1:1 z prototype/screens.jsx ReklamationModal.
 *  Form: Kategorie 2x2 + Beschreibung textarea + Fotos dropzone + Dringlichkeit
 *  + INSERT do service_tickets přes RLS. */

import { useState } from "react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { useAuth } from "@/auth/auth-provider";
import { createComplaint, type CreateComplaintInput } from "@/lib/fetchers";
import { t } from "@/i18n/de";
import type { PartnerOrder } from "@/lib/types";

type Category = CreateComplaintInput["category"];
type Urgency = CreateComplaintInput["urgency"];

const CATEGORIES: { id: Category; label: string }[] = [
  { id: "tech", label: t.reklamation.categories.tech },
  { id: "delivery", label: t.reklamation.categories.delivery },
  { id: "docs", label: t.reklamation.categories.docs },
  { id: "other", label: t.reklamation.categories.other },
];

const URGENCIES: { id: Urgency; label: string; tone: "blue" | "gold" | "amber" | "red" }[] = [
  { id: "low", label: t.reklamation.urgency.low, tone: "blue" },
  { id: "normal", label: t.reklamation.urgency.mid, tone: "gold" },
  { id: "high", label: t.reklamation.urgency.high, tone: "amber" },
  { id: "urgent", label: t.reklamation.urgency.critical, tone: "red" },
];

interface ReklamationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: PartnerOrder;
  onSuccess: () => void;
}

export function ReklamationModal({ open, onOpenChange, order, onSuccess }: ReklamationModalProps) {
  const { partner, user } = useAuth();
  const [category, setCategory] = useState<Category>("tech");
  const [urgency, setUrgency] = useState<Urgency>("normal");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCategory("tech");
    setUrgency("normal");
    setDescription("");
    setError(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    setError(null);
    if (description.trim().length < 30) {
      setError("Beschreibung muss mindestens 30 Zeichen enthalten.");
      return;
    }
    const email = user?.email || partner?.email || "";
    if (!email) {
      setError("Kein E-Mail-Account zugeordnet.");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createComplaint({
        dealId: order.id,
        category,
        description: description.trim(),
        urgency,
        partnerEmail: email,
      });
      toast.success(t.reklamation.submitOk(created.ticket_number));
      reset();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} width={520}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            {t.reklamation.eyebrow}
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
            {order.end_client_label || order.title || "—"}
          </h2>
          <div style={{ marginTop: 4, fontSize: 12.5, color: "var(--text-3)" }}>
            <span className="num">{order.title || order.id.slice(0, 8)}</span>
            {order.installation_city ? ` · ${order.installation_city}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          className="btn btn-ghost btn-sm"
          style={{ padding: 6 }}
          aria-label="Schließen"
        >
          <Ic.Close size={16} />
        </button>
      </div>

      <Field label={t.reklamation.fields.category}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 6,
            marginTop: 6,
          }}
        >
          {CATEGORIES.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                style={{
                  padding: "10px 12px",
                  fontSize: 13,
                  textAlign: "left",
                  borderRadius: 8,
                  background: active ? "var(--gold-12)" : "var(--bg-base-2)",
                  color: active ? "var(--gold-hi)" : "var(--text)",
                  border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                  cursor: "pointer",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </Field>

      <div style={{ marginTop: 16 }}>
        <Field label={`${t.reklamation.fields.description} *`}>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.reklamation.descriptionPlaceholder}
            style={{ height: 110, paddingTop: 10, resize: "none", lineHeight: 1.5 }}
            required
            minLength={30}
            disabled={submitting}
          />
        </Field>
      </div>

      <div style={{ marginTop: 16 }}>
        <Field label={t.reklamation.fields.photos}>
          <div
            style={{
              border: "1.5px dashed var(--border-strong)",
              borderRadius: "var(--r-md)",
              padding: "20px 14px",
              textAlign: "center",
              color: "var(--text-3)",
              fontSize: 13,
              cursor: "not-allowed",
              opacity: 0.7,
            }}
          >
            <Ic.Camera size={20} />
            <div style={{ marginTop: 6 }}>{t.reklamation.photosHint}</div>
            <div style={{ fontSize: 11, color: "var(--text-4)", marginTop: 2 }}>
              {t.reklamation.photosLimits} · in v0.2 verfügbar
            </div>
          </div>
        </Field>
      </div>

      <div style={{ marginTop: 16 }}>
        <Field label={t.reklamation.fields.urgency}>
          <div style={{ display: "flex", gap: 6 }}>
            {URGENCIES.map((u) => {
              const active = urgency === u.id;
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setUrgency(u.id)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    fontSize: 12,
                    fontWeight: 500,
                    borderRadius: 8,
                    background: active ? "var(--bg-card)" : "transparent",
                    color: active ? "var(--text)" : "var(--text-2)",
                    border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                    cursor: "pointer",
                  }}
                >
                  {u.label}
                </button>
              );
            })}
          </div>
          {urgency === "urgent" && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                fontSize: 12,
                color: "var(--amber)",
                background: "var(--amber-12)",
                border: "1px solid rgba(244,183,64,0.22)",
                borderRadius: 8,
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <Ic.Alert size={12} /> {t.reklamation.urgency.criticalNote}
            </div>
          )}
        </Field>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 14,
            padding: 10,
            fontSize: 12.5,
            color: "var(--red)",
            background: "var(--red-12)",
            border: "1px solid rgba(248,113,113,0.22)",
            borderRadius: "var(--r-md)",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 22,
          justifyContent: "flex-end",
        }}
      >
        <Button variant="ghost" onClick={close} disabled={submitting}>
          {t.reklamation.cancel}
        </Button>
        <Button variant="primary" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting && <Ic.Loader size={14} className="animate-spin" />}
          {t.reklamation.submit}
        </Button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          marginBottom: 6,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
